import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Save, UserRound } from "lucide-react";
import { userApi } from "../../backend/client";
import {
  invalidateCurrentUser,
  useCurrentUser,
} from "../../backend/queries/auth";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import TimezoneSelect from "../global/TimezoneSelect";
import { ChatEmailPreference } from "../profile/ChatEmailPreference";

const AgentProfile = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editUserData, setEditUserData] = useState({
    email: "",
    role: "",
    firstname: "",
    lastname: "",
    contactnumber: "",
    ianatimezonecode: "",
    dateofbirth: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const queryClient = useQueryClient();
  const { data: currentUser, isPending } = useCurrentUser();

  useEffect(() => {
    const userData = currentUser?.profile;
    if (!userData) return;
    setUser(userData);
    setEditUserData(userData);
    setIsLoading(false);
  }, [currentUser]);

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditUserData({ ...editUserData, [name]: value });
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    const {
      firstname,
      lastname,
      contactnumber,
      ianatimezonecode,
      dateofbirth,
    } = editUserData;

    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const { data: updatedUser, error } = await userApi.update(user.id, {
        expected_version: user.version,
        firstname,
        lastname,
        contactnumber,
        ianatimezonecode,
        dateofbirth: dateofbirth || null,
      });

      if (error) {
        setErrorMessage("Error updating details: " + error.message);
      } else if (updatedUser) {
        setUser(updatedUser);
        setEditUserData(updatedUser);
        await invalidateCurrentUser(queryClient);
        setSuccessMessage("Details successfully updated");
      }
    } catch (err) {
      setErrorMessage("Unexpected error: " + (err?.message || String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="p-4 sm:p-6">
        <div className="mx-auto flex max-w-4xl flex-col gap-5">
          <div>
            <h1 className="text-2xl font-semibold">My profile</h1>
            <p className="text-sm text-muted-foreground">
              Update the profile details used by the portal.
            </p>
          </div>

          {(errorMessage || successMessage) && (
            <div
              className={`rounded-md border px-4 py-3 text-sm ${
                errorMessage
                  ? "border-destructive/30 bg-destructive/10 text-destructive"
                  : "border-tone-success-border bg-tone-success-surface text-tone-success-foreground"
              }`}
            >
              {errorMessage || successMessage}
              <button
                type="button"
                className="ml-3 font-medium underline"
                onClick={() => {
                  setErrorMessage("");
                  setSuccessMessage("");
                }}
              >
                Dismiss
              </button>
            </div>
          )}

          <Card>
            <CardHeader className="p-4 sm:p-5">
              <CardTitle className="flex items-center gap-2">
                <UserRound className="h-5 w-5" />
                Profile information
              </CardTitle>
              <CardDescription>
                Email and role are managed by account access.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
              {(isLoading || isPending) && !user ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  Loading profile...
                </div>
              ) : (
                <form className="space-y-5" onSubmit={handleEditSubmit}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field
                      label="First name"
                      id="firstName"
                      name="firstname"
                      value={editUserData?.firstname || ""}
                      onChange={handleEditChange}
                    />
                    <Field
                      label="Last name"
                      id="lastName"
                      name="lastname"
                      value={editUserData?.lastname || ""}
                      onChange={handleEditChange}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <Field
                      label="Email"
                      id="email"
                      type="email"
                      value={editUserData?.email || user?.email || ""}
                      readOnly
                    />
                    <Field
                      label="Role"
                      id="role"
                      value={user?.role || editUserData?.role || ""}
                      readOnly
                    />
                  </div>

                  <Field
                    label="Phone number"
                    id="contactnumber"
                    name="contactnumber"
                    value={editUserData?.contactnumber || ""}
                    onChange={handleEditChange}
                  />

                  <div className="space-y-2">
                    <Label htmlFor="profile_timezone">Time zone</Label>
                    <TimezoneSelect
                      id="profile_timezone"
                      value={
                        editUserData?.ianatimezonecode ??
                        user?.ianatimezonecode ??
                        ""
                      }
                      onChange={handleEditChange}
                    />
                  </div>

                  <Field
                    label="Date of birth"
                    id="DOB"
                    name="dateofbirth"
                    type="date"
                    value={editUserData?.dateofbirth || ""}
                    onChange={handleEditChange}
                    required={false}
                  />

                  <div className="flex justify-end">
                    <Button type="submit" disabled={isLoading}>
                      <Save className="h-4 w-4" />
                      {isLoading ? "Saving..." : "Save changes"}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          <ChatEmailPreference />
        </div>
      </div>
    </>
  );
};

const Field = ({
  label,
  id,
  name,
  value,
  onChange,
  type = "text",
  readOnly = false,
  required = true,
}: any) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input
      id={id}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      required={required && !readOnly}
    />
  </div>
);

export default AgentProfile;
